import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import styles from './Button.module.css';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'small' | 'medium' | 'large';

interface CommonProps {
  variant?: Variant;
  size?: Size;
  /** Fills the available width. Used for the primary action on mobile. */
  block?: boolean;
  /** Adds a trailing arrow that nudges on hover. */
  withArrow?: boolean;
  children: ReactNode;
  className?: string;
}

function classesFor({ variant = 'primary', size = 'medium', block, className }: CommonProps) {
  return [
    styles.base,
    styles[variant],
    size !== 'medium' ? styles[size] : '',
    block ? styles.block : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
}

function Arrow() {
  return (
    <svg
      className={styles.arrow}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M3 8h9m0 0L8.5 4.5M12 8l-3.5 3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type ButtonProps = CommonProps & ButtonHTMLAttributes<HTMLButtonElement>;

export function Button({
  variant,
  size,
  block,
  withArrow,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={classesFor({ variant, size, block, className, children })}
      {...rest}
    >
      {children}
      {withArrow ? <Arrow /> : null}
    </button>
  );
}

type ButtonLinkProps = CommonProps & Omit<LinkProps, 'className'>;

/** A router link that looks like a button. Still a link, so it behaves like one. */
export function ButtonLink({
  variant,
  size,
  block,
  withArrow,
  children,
  className,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link className={classesFor({ variant, size, block, className, children })} {...rest}>
      {children}
      {withArrow ? <Arrow /> : null}
    </Link>
  );
}
